// Angular
import { BehaviorSubject, Observable, map, takeUntil } from 'rxjs';
import { Injectable, OnDestroy, EventEmitter } from '@angular/core';

// Models
import { JobData, Job, JobStatus } from '../models';
import { AppCleanup } from '../../../shared/models';

// Libraries
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';

import { Select, Store } from '@ngxs/store';

// Services
import { JobService } from './job.service';
import { RecordChange } from '../models/record-change';

// Store
import { JobState } from '../state/job.state';
import { JobActions } from '../state/job.action';

// Enums
import { TargetType } from '../enums/target-type.enum';
import { RecordChangeSourceType } from '../../../shared/enums/record-change-source-type.enum';

// Helpers
import { UtilityHelper } from '../../../shared/helpers';

@Injectable({
	providedIn: 'root',
})
export class JobHub extends AppCleanup implements OnDestroy {
	@Select(JobState.activeJobs) activeJobs$: Observable<Job[]>;
	@Select(JobState.filteredActiveJobs) filteredActiveJobs$: Observable<Job[]>;

	protected connectionEstablishedVar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
	connectionEstablished$ = this.connectionEstablishedVar.asObservable();
	connected: boolean = false;

	protected refreshFromDisconnectVar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
	refreshFromDisconnect$ = this.refreshFromDisconnectVar.asObservable();

	private _initialReconnectFired: boolean = false;
	private _hubConnection: HubConnection;
	reconnectStart: Date = null;
	private _keys: string[] = [];

	filteredActiveJobs: Job[];

	protected dataReceivedVar: BehaviorSubject<Array<JobData>> = new BehaviorSubject<Array<JobData>>(null);
	dataReceived$ = this.dataReceivedVar.asObservable(); // Triggers when data is received via signal R

	jobCreated$: EventEmitter<Job> = new EventEmitter<Job>(null); // Triggers when job is created via signal R
	jobStarted$: EventEmitter<Job> = new EventEmitter<Job>(null); // Triggers when job is started via signal R
	jobCompleted$: EventEmitter<Job> = new EventEmitter<Job>(null); // Triggers when job is completed via signal R
	recordChange$ = new EventEmitter<RecordChange>(null); // Triggers when record changes via signal R
	messageReceived$: EventEmitter<string> = new EventEmitter<string>(null); // Triggers when a message is received via signal R

	onReconnected: (connectionId?: string) => void;
	onInitialConnect: () => void;
	onReconnecting: (error?: Error) => void;
	onInitialConnecting: () => void;
	onClose: (error?: Error) => void;

	constructor(
		private jobService: JobService,
		private store: Store
	) {
		super();
		this.jobHubUrl = this.lvHubUrl;

		// this had to be binded to get _keys
		this.onReconnecting = this._onReconnecting.bind(this);
		this.onReconnected = this._onReconnected.bind(this);
		this.onInitialConnecting = this._onReconnecting.bind(this);
		this.onInitialConnect = this._onReconnected.bind(this);

		this.createConnection();
		this.getActiveJobsAndSubscribe();

		this.subscribeToJobData();

		this.filteredActiveJobs$.pipe(takeUntil(this.destroy)).subscribe((filteredActiveJobs) => {
			if (filteredActiveJobs) {
				this.filteredActiveJobs = filteredActiveJobs;
			}
		});
	}

	private createConnection() {
		this._hubConnection = new HubConnectionBuilder().configureLogging(LogLevel.Error).withUrl(this.jobHubUrl).withAutomaticReconnect().build();

		this._hubConnection.keepAliveIntervalInMilliseconds = this.isViewer ? 1000 : 5000;
		this._hubConnection.serverTimeoutInMilliseconds = this.isViewer ? 5000 : 15000;

		this.registerHubEvents();
	}

	private registerHubEvents() {
		if (this.onReconnected) this._hubConnection.onreconnected(this.onReconnected);

		if (this.onReconnecting) this._hubConnection.onreconnecting(this.onReconnecting);

		if (this.onClose) this._hubConnection.onclose(this.onClose);
	}

	startSendingData(keys: string[]) {
		this._hubConnection
			.invoke('StartSendingData', keys)
			.then(() => {
				this.connected = true;
				keys.forEach((key) => {
					if (!this._keys.find((k) => k === key)) {
						this._keys.push(key);
					}
				});
			})
			.catch((err) => console.error(err));
	}

	startConnection(keys: string[]): Promise<void> {
		return this._hubConnection
			.start()
			.then(() => {
				this.connectionEstablishedVar.next(true);

				if (this.onInitialConnect && this._initialReconnectFired) this.onInitialConnect();

				if (keys) this.startSendingData(keys);
			})
			.catch((reason) => {
				setTimeout(
					() => {
						if (this.onInitialConnecting && !this._initialReconnectFired) {
							this.onInitialConnecting();
							this._initialReconnectFired = true;
						}

						if (keys) this.startConnection(keys);
					},
					5000,
					keys
				);
			});
	}

	disconnect(keys: string[]) {
		console.log('Disconnected');
		this._hubConnection
			.invoke('StopSendingData', keys)
			.then(() => {
				if (this._hubConnection.state === HubConnectionState.Connected) {
					this._hubConnection.stop();
					this.connected = false;
				}
			})
			.catch((err) => console.error(err));
	}

	registerOnServerEvents() {
		this.registerHubEvents();

		//TJD = TransferJobData. Using TJD to reduce message size
		this._hubConnection.on(TargetType.TJD, (data: Array<JobData>) => {
			if (data) {
				data.map((d) => {
					d.d;
				});
			}
			this.dataReceivedVar.next(data);
		});

		this._hubConnection.on(TargetType.JOB_STARTED, (data: Job) => {
			if (data && data.status === JobStatus.InProgress) {
				let activeJob = this.filteredActiveJobs.find((job) => job.key === data.key);

				if (activeJob) {
					// May need to update the entire job, but what is sent with SignalR is only the basics. For now, we only need status.
					activeJob = UtilityHelper.deepCopy(activeJob);
					activeJob.status = data.status;
					this.store.dispatch(new JobActions.UpdateActiveJob(activeJob));
				}

				this.jobStarted$.emit(data);
			}
		});

		this._hubConnection.on(TargetType.JOB_COMPLETED, (data: Job) => {
			if (data && data.status === JobStatus.Completed) {
				this.jobCompleted$.emit(data);
			}
		});

		this._hubConnection.on(TargetType.JOB_CREATED, (data: Job) => {
			if (data && data.status === JobStatus.NotStarted) {
				this.jobCreated$.emit(data);
			}
		});

		this._hubConnection.on(TargetType.RECORD_CHANGE, (data: RecordChange) => {
			data.source = this.isViewer ? RecordChangeSourceType.LOCAL_VIEWER : RecordChangeSourceType.PORTAL;
			this.recordChange$.emit(data);
		});

		this._hubConnection.on(TargetType.DISPLAY_MESSAGE, (data: any) => {
			this.messageReceived$.emit(data);
		});
	}

	private getActiveJobsAndSubscribe() {
		this.registerOnServerEvents();
		this.startConnection(['All']);

		this.activeJobs$.pipe(takeUntil(this.destroy)).subscribe((activeJobs) => {
			if (activeJobs) {
				this.connectionEstablished$.pipe(takeUntil(this.destroy)).subscribe((est) => {
					if (est) {
						let keys: string[] = [];

						activeJobs.forEach((aj) => {
							keys.push(aj.key);
						});

						//For testing
						//keys = ["Austin 123_Unit401", "Austin 123_Unit402"];

						this.startSendingData(keys);
					}
				});
			}
		});
	}

	private _onReconnecting() {
		console.log('Reconnecting');
		this.reconnectStart = new Date();
	}

	private _onReconnected() {
		console.log('Reconnected ', this._keys);

		this.startSendingData(['All']);

		this.startSendingData(this._keys);

		if (this.reconnectStart) {
			let date = new Date();
			let timeDiff = date.getTime() - this.reconnectStart.getTime();
			timeDiff = Math.floor(timeDiff / (1000 * 60));
			this.reconnectStart = null;

			if (timeDiff > 0) {
				this.refreshFromDisconnectVar.next(true);
			}
		}
	}

	subscribeToJobData() {
		this.dataReceived$
			.pipe(
				map((data: Array<JobData>) => {
					if (data && data.length > 0) {
						data.forEach((d) => {
							d.d = new Date(d.d);
						});
					}
					return data;
				}),
				takeUntil(this.destroy)
			)
			.subscribe((data: Array<JobData>) => {
				if (data && data.length > 0) {
					this.jobService.addPointToListener(data);
				}
			});
	}

	ngOnDestroy() {
		this.activeJobs$.pipe(takeUntil(this.destroy)).subscribe((activeJobs) => {
			if (activeJobs) {
				let keys: string[] = [];

				activeJobs.forEach((aj) => {
					keys.push(aj.key);
				});

				this.disconnect(keys);
			}
		});
	}

	emitRecordChange(data: RecordChange) {
		this.recordChange$.emit(data);
	}
}
